import React, { useState } from "react";
import { ethers } from "ethers";
import { useWeb3 } from "../context/Web3Context";
import { useUserProfile, useUSDT, useTx } from "../hooks/useContract";
import { useNotification } from "../hooks/useNotification";
import { formatUSDT } from "../utils/format";
import { CONTRACT_ADDRESS } from "../config/contract";
import { PACKAGES } from "../config/constants";
import { parseError } from "../utils/errorParser";
import { recordOfferEvent } from "../hooks/useOffers";
import Button from "../components/Common/Button";
import FullScreenLoader from "../components/Loader/FullScreenLoader";
import Speedometer from "../components/Charts/Speedometer";

// Each package comes with a dedicated crypto-education eBook that unlocks
// once the user has activated the package. Files live in /public/ so they
// are served from the same origin, and we embed them read-only in a modal.
const PACKAGE_EBOOKS = {
  1: { title: "Crypto Starter",                 file: "888_Global_Crypto_Starter_eBook.pdf" },
  2: { title: "Blockchain Explorer",            file: "888_Global_Blockchain_Explorer_18_USD_eBook.pdf" },
  3: { title: "Blockchain Wallet Mastery",      file: "888_Global_Blockchain_Wallet_Mastery_26_USD_eBook.pdf" },
  4: { title: "Crypto Transactions Masterclass",file: "888_Global_Crypto_Transactions_Masterclass_48_USD_eBook.pdf" },
  5: { title: "Web3 Fundamentals",              file: "888_Global_Web3_Fundamentals_68_USD_eBook.pdf" },
  6: { title: "DeFi Token Economy",             file: "888_Global_DeFi_Token_Economy_88_USD_eBook.pdf" },
  7: { title: "Advanced Web3",                  file: "888_Global_Advanced_Web3_188_USD_eBook.pdf" },
  8: { title: "Crypto Web3 Masterclass",        file: "888_Global_Crypto_Web3_Masterclass_444_USD_eBook.pdf" },
};

function Packages() {
  const notify = useNotification();
  const { account, connect, getLogic, getUsdt } = useWeb3();
  const { loading, isRegistered, packages, directRefsCountPerPackage, reload } = useUserProfile();
  const { balance, allowance, reload: reloadUsdt } = useUSDT();
  const { pending } = useTx();
  const [busyId, setBusyId] = useState(0);
  // Which package's eBook is currently open (0 = none)
  const [ebookOpen, setEbookOpen] = useState(0);

  if (!account) {
    return (
      <div className="empty-state animate-fade-in-up">
        <div className="empty-state-icon">◇</div>
        <div className="empty-state-text mb-lg">Connect wallet to view packages.</div>
        <Button variant="primary" onClick={connect}>Connect Wallet</Button>
      </div>
    );
  }
  if (loading) return <FullScreenLoader text="Loading Packages" />;
  if (!isRegistered) {
    return (
      <div className="empty-state animate-fade-in-up">
        <div className="empty-state-icon">◇</div>
        <div className="empty-state-text">Register first to see your packages.</div>
      </div>
    );
  }

  // Determine the "next" activatable package
  const nextIdx = packages.findIndex((p) => !p.isActive);

  const doAction = async (pkg, action) => {
    const price = ethers.utils.parseUnits(String(pkg.price), 18);
    if (balance.lt(price)) return notify.error("Not enough USDT");

    const logic = getLogic();
    const usdt = getUsdt();
    if (!logic || !usdt) return notify.error("Logic contract not configured");

    setBusyId(pkg.id);
    try {
      if (allowance.lt(price)) {
        notify.info("Approving USDT...");
        const ax = await usdt.approve(CONTRACT_ADDRESS, ethers.constants.MaxUint256);
        await ax.wait();
        notify.success("Approved");
        reloadUsdt();
      }
      const call =
        action === "activate"
          ? () => logic.activatePackage(pkg.id)
          : () => logic.retopupPackage(pkg.id);
      notify.info(action === "activate" ? "Activating..." : "Re-topping up...");
      const tx = await call();
      const receipt = await tx.wait();
      notify.success(action === "activate" ? "Package activated!" : "Re-topup done!");
      // Record for any active reward offer (fire-and-forget)
      recordOfferEvent({
        wallet: account,
        action,                             // "activate" or "retopup"
        packageId: pkg.id,
        amount: String(pkg.price),
        txHash: receipt?.transactionHash || tx?.hash || "",
      });
      reload();
      reloadUsdt();
    } catch (e) {
      notify.error(parseError(e));
    } finally {
      setBusyId(0);
    }
  };

  return (
    <div className="animate-fade-in-up">
      {/* USDT wallet balance */}
      <div style={{
        background: "#0a0e24",
        border: "1px solid var(--cyan-border)",
        borderRadius: 14,
        padding: "14px 16px",
        marginBottom: 16,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        position: "relative",
      }}>
        <div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>
            Wallet Balance
          </div>
          <div style={{
            fontFamily: "'Sora', 'Inter', sans-serif",
            fontSize: 26,
            fontWeight: 700,
            color: "var(--cyan)",
            letterSpacing: "-0.02em",
            lineHeight: 1.1,
            marginTop: 4,
            fontVariantNumeric: "tabular-nums",
          }}>
            ${formatUSDT(balance)}
          </div>
        </div>
        <div style={{
          padding: "6px 12px",
          background: "rgba(0, 229, 255, 0.1)",
          border: "1px solid var(--cyan-border)",
          borderRadius: 20,
          fontSize: 11,
          fontWeight: 700,
          color: "var(--cyan)",
          fontFamily: "var(--font-display)",
          letterSpacing: "0.08em",
        }}>
          USDT
        </div>
      </div>

      <div className="section-header">
        <div className="section-title">My Packages</div>
        <span className="badge badge-cyan">{packages.filter((p) => p.isActive).length} / 8 Active</span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {packages.map((pkg, idx) => {
          const isNext = !pkg.isActive && idx === nextIdx;
          const isLocked = !pkg.isActive && !isNext;
          const busy = busyId === pkg.id;

          // Speedometer values — trust on-chain ceilingLimit (contract now always sets a real value)
          const invested = parseFloat(ethers.utils.formatUnits(pkg.totalInvested || 0, 18));
          const earned = parseFloat(ethers.utils.formatUnits(pkg.totalEarned || 0, 18));
          const ceiling = parseFloat(ethers.utils.formatUnits(pkg.ceilingLimit || 0, 18));
          const available = Math.max(0, ceiling - earned);
          // Retopup count = totalInvested / packagePrice - 1 (first is the activation)
          const retopups = pkg.price > 0 ? Math.max(0, Math.round(invested / pkg.price) - 1) : 0;

          let className = "pkg-card";
          if (pkg.isActive) className += " active";
          else if (isNext) className += " next";
          else className += " locked";

          // Status label + colors, resolved once for inline use in the header
          const statusInfo = pkg.isActive
            ? { label: "ACTIVE", bg: "rgba(34,255,160,0.12)",   color: "var(--success)",       border: "rgba(34,255,160,0.4)" }
            : isNext
            ? { label: "NEXT",   bg: "rgba(139,92,246,0.15)",   color: "var(--violet-bright)", border: "rgba(139,92,246,0.4)" }
            : { label: "LOCKED", bg: "rgba(139,155,209,0.08)",  color: "var(--text-muted)",    border: "var(--border-strong)" };

          return (
            <div key={pkg.id} className={className}>
              <div className="pkg-header-row" style={{ alignItems: "flex-start" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div className="pkg-num-badge">{pkg.id}</div>
                  <div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)", letterSpacing: "0.05em", textTransform: "uppercase" }}>Package</div>
                    <div className="pkg-price">${pkg.price}</div>
                  </div>
                </div>
                {/* Right side: status pill on top, directs chip below (only when active).
                    Ceiling tier upgrades at 4 directs (→4×) and 10 directs (→5×). */}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
                  <span
                    style={{
                      display: "inline-block",
                      padding: "3px 10px",
                      borderRadius: 6,
                      background: statusInfo.bg,
                      color: statusInfo.color,
                      border: `1px solid ${statusInfo.border}`,
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      fontFamily: "var(--font-display)",
                    }}
                  >
                    {statusInfo.label}
                  </span>
                  {pkg.isActive && (() => {
                    const dc = directRefsCountPerPackage?.[pkg.id - 1] || 0;
                    const nextUpgrade = dc < 4 ? 4 : dc < 10 ? 10 : null;
                    const isHot = dc >= 4;
                    return (
                      <div
                        style={{
                          display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2,
                          padding: "6px 10px",
                          borderRadius: 8,
                          background: isHot
                            ? "linear-gradient(135deg, rgba(0,229,255,0.15), rgba(139,92,246,0.15))"
                            : "rgba(139,155,209,0.06)",
                          border: `1px solid ${isHot ? "var(--cyan-border)" : "var(--border)"}`,
                          minWidth: 68,
                        }}
                      >
                        <div style={{
                          fontFamily: "var(--font-display)",
                          fontSize: 18,
                          fontWeight: 700,
                          color: isHot ? "var(--cyan)" : "var(--text-secondary)",
                          lineHeight: 1,
                          fontVariantNumeric: "tabular-nums",
                        }}>
                          {dc}
                        </div>
                        <div style={{
                          fontSize: 9,
                          color: "var(--text-muted)",
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          fontWeight: 600,
                        }}>
                          {dc === 1 ? "Direct" : "Directs"}
                        </div>
                        {nextUpgrade && (
                          <div style={{ fontSize: 8, color: "var(--violet-bright)", fontWeight: 600, marginTop: 2 }}>
                            {nextUpgrade - dc} to {nextUpgrade === 4 ? "4×" : "5×"}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {pkg.isActive && (
                <>
                  <div className="pkg-gauge-wrap">
                    <Speedometer
                      value={earned}
                      max={ceiling || 1}
                      size={170}
                      label={`$${earned.toFixed(1)}`}
                      sublabel="Earned"
                      color="#00e5ff"
                    />
                  </div>

                  {/* Ceiling tier + retopup count */}
                  {(() => {
                    const tier = pkg.ceilingTier || 3;
                    const isMax = tier >= 5;
                    const isUpgraded = tier >= 4;
                    return (
                      <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 10, marginTop: -8 }}>
                        <span
                          className="badge"
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            background: isMax
                              ? "linear-gradient(135deg, var(--magenta), var(--violet))"
                              : isUpgraded
                              ? "linear-gradient(135deg, var(--cyan), var(--violet))"
                              : "rgba(0, 229, 255, 0.12)",
                            color: isUpgraded ? "white" : "var(--cyan)",
                            border: isUpgraded ? "none" : "1px solid var(--cyan-border)",
                            letterSpacing: "0.05em",
                            padding: "3px 10px",
                            boxShadow: isMax
                              ? "0 0 10px rgba(236, 72, 153, 0.6)"
                              : isUpgraded
                              ? "0 0 8px rgba(0, 229, 255, 0.5)"
                              : "none",
                          }}
                        >
                          {tier}× CEILING{isMax ? " MAX" : ""}
                        </span>
                        {retopups > 0 && (
                          <span className="badge badge-violet" style={{ fontSize: 10 }}>
                            {retopups}× RE-TOPUP{retopups > 1 ? "S" : ""}
                          </span>
                        )}
                      </div>
                    );
                  })()}

                  <div className="pkg-gauge-stats">
                    <div className="pkg-gauge-stat">
                      <div className="pkg-gauge-stat-label">Ceiling</div>
                      <div className="pkg-gauge-stat-value">${ceiling.toFixed(0)}</div>
                    </div>
                    <div className="pkg-gauge-stat">
                      <div className="pkg-gauge-stat-label">Available</div>
                      <div className="pkg-gauge-stat-value" style={{ color: "var(--cyan)" }}>${available.toFixed(1)}</div>
                    </div>
                    <div className="pkg-gauge-stat">
                      <div className="pkg-gauge-stat-label">Invested</div>
                      <div className="pkg-gauge-stat-value">${invested.toFixed(0)}</div>
                    </div>
                  </div>

                  {/* Retopup enabled only when earned >= package price */}
                  {(() => {
                    const canRetopup = earned >= pkg.price;
                    return (
                      <>
                        <button
                          className="btn btn-retopup btn-full"
                          style={{ marginTop: 16, opacity: canRetopup ? 1 : 0.4 }}
                          disabled={busy || !canRetopup}
                          onClick={() => doAction(pkg, "retopup")}
                          title={canRetopup ? "" : `Earn $${pkg.price} first to unlock re-topup`}
                        >
                          {busy ? <span className="spinner" /> : null}
                          Re-topup ${pkg.price}
                        </button>
                        {!canRetopup && (
                          <div style={{ marginTop: 8, fontSize: 11, color: "var(--text-muted)", textAlign: "center" }}>
                            Earn <span style={{ color: "var(--cyan)", fontWeight: 600 }}>${(pkg.price - earned).toFixed(2)}</span> more to unlock
                          </div>
                        )}
                      </>
                    );
                  })()}

                  {/* Crypto education eBook — unlocked by this package's activation */}
                  {PACKAGE_EBOOKS[pkg.id] && (
                    <button
                      type="button"
                      onClick={() => setEbookOpen(pkg.id)}
                      style={{
                        marginTop: 10,
                        width: "100%",
                        padding: "10px 14px",
                        borderRadius: 10,
                        background: "linear-gradient(135deg, rgba(0,229,255,0.08), rgba(139,92,246,0.08))",
                        border: "1px solid var(--cyan-border)",
                        color: "var(--cyan)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                        cursor: "pointer",
                        fontFamily: "var(--font-display)",
                        fontSize: 12,
                        fontWeight: 600,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        transition: "all 0.15s ease",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--cyan)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--cyan-border)"; }}
                    >
                      <span style={{ fontSize: 14 }}>📖</span>
                      Read {PACKAGE_EBOOKS[pkg.id].title}
                    </button>
                  )}
                </>
              )}

              {isNext && (
                <>
                  <div style={{ padding: "16px 0", textAlign: "center" }}>
                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12 }}>
                      Activate this package for ${pkg.price} USDT
                    </div>
                  </div>
                  <Button
                    variant="primary"
                    size="lg"
                    full
                    loading={busy}
                    onClick={() => doAction(pkg, "activate")}
                  >
                    Activate ${pkg.price}
                  </Button>
                </>
              )}

              {isLocked && (
                <div style={{ padding: "20px 0", textAlign: "center" }}>
                  <div style={{ fontSize: 24, opacity: 0.4, marginBottom: 6 }}>🔒</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                    Activate Package {idx} first
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* eBook reader — opens when user clicks "Read ..." on an active package.
          The PDF loads inside an iframe pointing at /public/, with the viewer's
          download/print toolbar suppressed via URL fragments. Reader-only. */}
      {ebookOpen > 0 && PACKAGE_EBOOKS[ebookOpen] && (
        <EbookModal
          packageId={ebookOpen}
          info={PACKAGE_EBOOKS[ebookOpen]}
          onClose={() => setEbookOpen(0)}
        />
      )}
    </div>
  );
}

// ============================================================
// eBook modal — full-screen reader with PDF embedded.
// We disable Chrome/Edge's built-in PDF toolbar via `#toolbar=0`
// to keep the UX focused on reading. Users can still find the raw
// URL in network tools, but no download/share buttons surface in-app.
// ============================================================
function EbookModal({ packageId, info, onClose }) {
  // Lock body scroll + ESC-to-close while open
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  // #toolbar=0&navpanes=0&scrollbar=1 hides the download/print toolbar
  // in Chromium-based PDF viewers (Chrome, Edge, Brave).
  const src = `/${info.file}#toolbar=0&navpanes=0&scrollbar=1&view=FitH`;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.88)",
        backdropFilter: "blur(8px)",
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        padding: 10,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          background: "#0a0e24",
          border: "1px solid var(--cyan-border)",
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: "0 0 40px rgba(0,229,255,0.2)",
        }}
      >
        {/* Header */}
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 16px",
          borderBottom: "1px solid var(--border)",
          background: "linear-gradient(135deg, rgba(0,229,255,0.08), rgba(139,92,246,0.08))",
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <div style={{
              width: 32, height: 32,
              borderRadius: 8,
              background: "linear-gradient(135deg, var(--cyan), var(--violet))",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 16,
              flexShrink: 0,
            }}>📖</div>
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontSize: 10,
                color: "var(--text-muted)",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                fontWeight: 600,
              }}>
                Package {packageId} · Education
              </div>
              <div style={{
                fontFamily: "var(--font-display)",
                fontSize: 15,
                fontWeight: 700,
                color: "var(--text-primary)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}>
                {info.title}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: 36, height: 36,
              borderRadius: 8,
              background: "rgba(255, 77, 109, 0.1)",
              border: "1px solid rgba(255, 77, 109, 0.3)",
              color: "var(--error)",
              fontSize: 20,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              lineHeight: 1,
            }}
            aria-label="Close eBook"
          >
            ×
          </button>
        </div>

        {/* PDF viewer */}
        <div style={{ flex: 1, position: "relative", background: "#1a1a1a" }}>
          <iframe
            src={src}
            title={info.title}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              display: "block",
            }}
          />
        </div>

        {/* Footer hint */}
        <div style={{
          padding: "8px 16px",
          borderTop: "1px solid var(--border)",
          fontSize: 10,
          color: "var(--text-muted)",
          textAlign: "center",
          letterSpacing: "0.03em",
          flexShrink: 0,
        }}>
          Read-only · tap outside or press ESC to close
        </div>
      </div>
    </div>
  );
}

export default Packages;
